import { useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash.debounce";
import { fetchData } from "../lib/dataCache";

const MIN_CHARS = 3;
const DEBOUNCE_MS = 200;
// Matches the API's cap on `text`; longer input is cut from the front, where nothing is being typed.
const MAX_CHARS = 300;
// An ambiguous span ("Democrat") would otherwise flood the palette.
const MAX_TERMS_PER_SPAN = 4;

const ageTerm = (match, age) => ({
  ...age,
  display: `Ages ${match.bands.join(", ")}`,
  originalDisplay: age.display,
  bandSelection: { isAll: false, selectedPresets: match.bands, customRange: null },
  span: { start: match.start, end: match.end },
  matchedText: match.text,
  distance: 0,
});

const plainTerms = (match, byId) =>
  match.termIds
    .map((id) => byId.get(id))
    .filter(Boolean)
    .slice(0, MAX_TERMS_PER_SPAN)
    .map((term) => ({
      ...term,
      span: { start: match.start, end: match.end },
      matchedText: match.text,
      distance: match.distance,
    }));

// Suggestions from GET /api/search/interpret for the span ending at the cursor. Each carries the
// `span` it replaces on commit. A failed request yields nothing: the palette still has its own matching.
export function useInterpretedTerms(text, termIndex) {
  const [result, setResult] = useState({ text: "", matches: [] });
  const latestRequest = useRef(0);

  const debouncedLookup = useMemo(
    () =>
      debounce(async (value) => {
        latestRequest.current += 1;
        const requestId = latestRequest.current;
        const sent = value.slice(-MAX_CHARS);
        const offset = value.length - sent.length;
        const matches = await fetchData(`/api/search/interpret?text=${encodeURIComponent(sent)}`)
          .then((body) => (body?.matches ?? []).map((m) => ({ ...m, start: m.start + offset, end: m.end + offset })))
          .catch(() => []);
        if (requestId === latestRequest.current) setResult({ text: value, matches });
      }, DEBOUNCE_MS),
    []
  );

  useEffect(() => {
    if ((text || "").trim().length < MIN_CHARS) {
      latestRequest.current += 1;
      return undefined;
    }
    debouncedLookup(text);
    return () => debouncedLookup.cancel();
  }, [text, debouncedLookup]);

  return useMemo(() => {
    // A response for older text has spans that no longer line up with what is on screen.
    const atCursor = text && result.text === text
      ? result.matches.find((m) => m.end === text.trimEnd().length)
      : null;
    const byId = new Map(termIndex.map((term) => [term.id, term]));
    const age = atCursor?.kind === "age_range" ? byId.get(atCursor.termIds[0]) : null;
    return !atCursor
      ? []
      : atCursor.kind === "age_range"
        ? age && atCursor.bands?.length ? [ageTerm(atCursor, age)] : []
        : plainTerms(atCursor, byId);
  }, [text, result, termIndex]);
}
